/**
 * Profile accordion section
 *
 * Wraps MemberProfileForm with the standard autosave pattern:
 *   - text fields: debounced trigger on change, save-immediately on blur
 *   - status surfaced via AutosaveStatus (yellow dirty / green saved)
 *   - flushOnUnmount so closing the modal mid-edit still persists
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useAutoSave } from '@hooks/useAutoSave';
import { Box } from '@mui/material';

import { MemberProfileForm } from '../MemberProfileForm';

import type { ProfileFormData } from './usePersonCard';

interface ProfileSectionProps {
  initialData: ProfileFormData;
  onSave: (data: ProfileFormData) => Promise<void>;
  disabled?: boolean;
}

const TEXT_FIELDS = new Set<keyof ProfileFormData>([
  'first_name',
  'last_name',
  'email',
  'mobile_phone_num',
]);

export function ProfileSection({ initialData, onSave, disabled = false }: ProfileSectionProps) {
  // Local form state, seeded from props. Re-syncs if the upstream hydration
  // changes (e.g., member detail loaded after first render).
  const [formData, setFormData] = useState<ProfileFormData>(initialData);
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const pendingDataRef = useRef<ProfileFormData | null>(null);

  const performSave = useCallback(
    async (data: ProfileFormData) => {
      await onSave(data);
    },
    [onSave],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt, hasPendingSave } = useAutoSave({
    onSave: performSave,
    debounceMs: 2000,
    flushOnUnmount: true,
  });

  const handleChange = useCallback(
    (field: string, value: string) => {
      const key = field as keyof ProfileFormData;
      const next = { ...formData, [key]: value };
      setFormData(next);
      pendingDataRef.current = next;

      // Mobile phone field is rendered via MuiTelInput which doesn't trigger a
      // blur — it fires onChange on every keystroke through the formatter. Use
      // debounced save for it. Plain text fields also debounce, then blur
      // saves immediately (handled below). Treat unknown fields as text.
      if (TEXT_FIELDS.has(key)) {
        triggerAutoSave(next);
      } else {
        saveImmediately(next);
      }
    },
    [formData, saveImmediately, triggerAutoSave],
  );

  const handleBlur = useCallback(() => {
    if (pendingDataRef.current) {
      saveImmediately(pendingDataRef.current);
      pendingDataRef.current = null;
    }
  }, [saveImmediately]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <AutosaveStatus
          status={status}
          isDirty={hasPendingSave()}
          lastSavedAt={lastSavedAt}
          verbose
        />
      </Box>
      <Box onBlur={handleBlur}>
        <MemberProfileForm
          firstName={formData.first_name}
          lastName={formData.last_name}
          email={formData.email}
          phone={formData.mobile_phone_num}
          onChange={handleChange}
          disabled={disabled}
        />
      </Box>
    </Box>
  );
}
